import {Button, Content, CrossIcon, DropDown, Input, List, ListGroup, ListItem, SearchIcon, Trigger} from '@kit';

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useApolloClient} from '@apollo/client/react';

import {SearchDocument, SearchQuery, SearchQueryVariables, SearchSection} from '../../generated/types';
import {buildUrl, getRoute, useAppRouter, useRoute} from '../../lib/routes';
import {cls} from '../utils';
import {SearchHitView} from './search-hit';
import {searchHitHref} from './search-hit-route';
import {searchSectionIcon, searchSectionName} from './search-sections';

import css from './site-search.module.css';

/** Поиск начинается со второго символа и после паузы в наборе */
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE = 200;
const SEARCH_SECTIONS = new Set<string>(Object.values(SearchSection));
/** Сколько записей раздела показать в выпадающем списке; остальное — на странице поиска */
const HITS_PER_SECTION = 4;

type Groups = SearchQuery['searchResult']['groups'];

/** «/» не перехватываем, когда пользователь печатает в другом поле */
function isEditable(target: EventTarget | null): boolean {
	const element = target as HTMLElement | null;
	return !!element && (element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName));
}

/** В русской раскладке та же клавиша даёт точку, поэтому смотрим на саму клавишу, а не на символ */
const isSearchHotkey = (e: KeyboardEvent) => e.code === 'Slash' || e.key === '/';

/**
 * Поиск по сайту в шапке: результаты по мере ввода, сгруппированные по разделам в порядке релевантности.
 * Enter без выбранного результата открывает страницу поиска, «/» ставит фокус в поле.
 */
type Props = {
	className?: string;
	/** раскрытое на узком экране поле занимает всю шапку — остальным её элементам нужно уступить место */
	onExpandedChange?: (expanded: boolean) => void;
};

export const SiteSearch: React.FC<Props> = ({className, onExpandedChange}) => {
	const router = useAppRouter();
	const routing = useRoute();
	const client = useApolloClient();

	const [query, setQuery] = useState('');
	const [open, setOpen] = useState(false);
	const [groups, setGroups] = useState<Groups | null>(null);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);
	const [correctedQuery, setCorrectedQuery] = useState<string | undefined>();
	/** на узком экране поле раскрывается поверх шапки */
	const [expanded, setExpanded] = useState(false);

	const inputRef = useRef<HTMLInputElement>(null);

	const text = query.trim();
	const allHref = buildUrl('search', {q: text});
	const section = routing?.routeName ? getRoute(routing.routeName).section : undefined;
	const prefer = section && SEARCH_SECTIONS.has(section) ? (section as SearchSection) : undefined;

	// запрос к API — когда поле в фокусе и пользователь перестал печатать
	useEffect(() => {
		if (!open || text.length < MIN_QUERY_LENGTH) {
			setLoading(false);
			if (text.length < MIN_QUERY_LENGTH) setGroups(null);
			return;
		}
		let cancelled = false;
		setLoading(true);
		const timer = setTimeout(() => {
			client
				.query<SearchQuery, SearchQueryVariables>({
					query: SearchDocument,
					variables: {query: text, limit: HITS_PER_SECTION, prefer},
				})
				.then(({data}) => {
					if (cancelled) return;
					// с errorPolicy: 'all' сетевая ошибка приходит без data, а не исключением
					setFailed(!data?.searchResult);
					setCorrectedQuery(data?.searchResult.correctedQuery);
					setGroups(data?.searchResult.groups ?? []);
				})
				.catch(() => {
					if (cancelled) return;
					setFailed(true);
					setGroups([]);
				})
				.finally(() => !cancelled && setLoading(false));
		}, DEBOUNCE);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [open, text, client, prefer]);

	// на странице поиска в поле запрос из адреса; после перехода на другую страницу поле очищается
	const urlQuery = routing?.routeName === 'search' && typeof router?.query.q === 'string' ? router.query.q : '';
	useEffect(() => {
		setQuery(urlQuery);
	}, [urlQuery, router?.asPath]);

	useEffect(() => {
		onExpandedChange?.(expanded);
	}, [expanded, onExpandedChange]);

	const collapse = useCallback(() => {
		setOpen(false);
		setExpanded(false);
		inputRef.current?.blur();
	}, []);

	useEffect(() => {
		router?.events.on('routeChangeStart', collapse);
		return () => router?.events.off('routeChangeStart', collapse);
	}, [router?.events, collapse]);

	const focusInput = useCallback(() => {
		setExpanded(true);
		inputRef.current?.focus();
		if (document.activeElement !== inputRef.current) setTimeout(() => inputRef.current?.focus());
	}, []);

	// «/» (в русской раскладке «.») с любого места страницы — к поиску
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!isSearchHotkey(e) || e.ctrlKey || e.metaKey || e.altKey || isEditable(e.target)) return;
			e.preventDefault();
			focusInput();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [focusInput]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				if (e.defaultPrevented) return;
				e.preventDefault();
				if (text) router?.push(allHref);
				return;
			}
			if (e.key === 'Escape' && !open) collapse();
		},
		[text, allHref, router, open, collapse],
	);

	// на узком экране поле в фокусе занимает всю шапку; список открывают клик и ввод, а не сам фокус
	const handleFocus = useCallback(() => setExpanded(true), []);

	useEffect(() => {
		if (!expanded) return;
		const handlePointerDown = (event: PointerEvent) => {
			const root = inputRef.current?.closest('[role="search"]');
			if (root && !root.contains(event.target as Node)) setExpanded(false);
		};
		document.addEventListener('pointerdown', handlePointerDown);
		return () => document.removeEventListener('pointerdown', handlePointerDown);
	}, [expanded]);

	const handleBlur = useCallback((event: React.FocusEvent) => {
		const root = event.currentTarget.closest('[role="search"]');
		if (!root?.contains(event.relatedTarget as Node | null)) setExpanded(false);
	}, []);

	const handleClear = useCallback(() => {
		setQuery('');
		setGroups(null);
		focusInput();
	}, [focusInput]);

	const showResults = open && text.length >= MIN_QUERY_LENGTH && (groups !== null || loading);

	return (
		<DropDown
			role="search"
			className={cls(css.siteSearch, expanded && css.expanded, className)}
			open={showResults}
			onOpenChange={setOpen}
			inputValue={query}
			onInputChange={setQuery}
		>
			<Button
				appearance="subtle"
				shape="circular"
				icon={<SearchIcon />}
				className={css.toggle}
				aria-label="Поиск по справочнику"
				onClick={focusInput}
			/>

			<Trigger>
				<Input
					setRef={inputRef}
					appearance="filled"
					shape="circular"
					className={css.field}
					boxClassName={css.box}
					contentBefore={<SearchIcon width={16} height={16} />}
					type="search"
					placeholder="Поиск по справочнику"
					aria-label="Поиск по справочнику"
					autoComplete="off"
					enterKeyHint="search"
					disableClear
					onChange={setQuery}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					buttons={
						query ? (
							<Button
								appearance="subtle"
								shape="circular"
								size="small"
								icon={<CrossIcon width={14} height={14} />}
								aria-label="Очистить поиск"
								onClick={handleClear}
							/>
						) : (
							<kbd className={css.hotkey} title="Нажмите «/», чтобы перейти к поиску">
								/
							</kbd>
						)
					}
				/>
			</Trigger>

			<Content width="trigger">
				{!!groups?.length && correctedQuery && (
					<div className={css.corrected}>Показаны результаты по запросу «{correctedQuery}»</div>
				)}
				{!!groups?.length && (
					<List className={css.results} aria-label="Результаты поиска">
						{groups.map((group) => (
							<ListGroup
								key={group.section}
								titleClassName={css.groupTitle}
								title={
									<>
										<span className={css.groupIcon}>{searchSectionIcon(group.section)}</span>
										{searchSectionName(group.section)}
										<span className={css.groupTotal}>{group.total}</span>
									</>
								}
							>
								{group.hits.map((hit) => (
									<ListItem
										key={`${hit.entity}:${hit.id}`}
										value={`${group.section}:${hit.entity}:${hit.id}`}
										href={searchHitHref(hit)}
										buttonClassName={css.option}
									>
										<SearchHitView hit={hit} snippet="auto" />
									</ListItem>
								))}
							</ListGroup>
						))}
						<ListItem value="all" href={allHref} buttonClassName={cls(css.option, css.all)}>
							Все результаты по запросу «{text}»
						</ListItem>
					</List>
				)}
				{loading && !groups?.length && <div className={css.message}>Ищем…</div>}
				{!loading && failed && (
					<div className={css.message}>Поиск сейчас не работает. Попробуйте чуть позже.</div>
				)}
				{!loading && !failed && groups && !groups.length && (
					<div className={css.message}>
						Ничего не нашлось. Попробуйте другое написание или название на английском.
					</div>
				)}
			</Content>
		</DropDown>
	);
};
